import { useRef, useState } from "react";
import { ButtonGroup, Form, IconButton, Tooltip, Whisper } from "rsuite";
import { FormInstance } from "rsuite/esm/Form";
import { SvgAol, SvgGmail, SvgOutlook, SvgYahoo } from "../../assets/svg";
import { ApiSettings } from "../../core/apis";
import { Button } from "../button.component";
import { PasswordInput, TextInput } from "../textInput.component";
import { showErrorToast, showSuccessToast } from "../toast.component";
import { ModalGeneric } from "./ModalGeneric";
import { ISenderEmailApi } from "../../models/api";
import { StorageAuth } from "../../core/localStorage";
import { EnumEmailProvider } from "../../libs";
import { UtilityValidator } from "../../utils/validatorUtils";
interface IProps {
  show: boolean;
  companyId: string;
  toggleModal: () => void;
  onSave: Function;
}

const videoLink = {
  gmail:
    "https://www.youtube-nocookie.com/embed/CLzKb99OROQ?si=7VDy25aQBxL6k43r",
  outlook:
    "https://www.youtube-nocookie.com/embed/fqimk_W1cgQ?si=v_xx0tBNwfFjVN95",
  aol: "https://www.youtube-nocookie.com/embed/CEz0tZgnvvo?si=knzbty7BFY7Y59CM",
  yahoo:
    "https://www.youtube-nocookie.com/embed/CYG-krm0d3U?si=f6738SolmKNGQySt",
};

export function ModalAddEmail({
  show,
  toggleModal,
  onSave,
  companyId,
}: IProps) {
  const formRef = useRef<FormInstance | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [information, setInformation] = useState<ISenderEmailApi>({
    companyId: companyId || StorageAuth.CompanyId,
  } as ISenderEmailApi);
  const isDisabled = !information?.senderEmail || !information?.appPassword;

  const getVideoLink = () => {
    if (information.provider === EnumEmailProvider.Gmail) {
      return videoLink.gmail;
    } else if (information.provider === EnumEmailProvider.Outlook) {
      return videoLink.outlook;
    } else if (information.provider === EnumEmailProvider.Yahoo) {
      return videoLink.yahoo;
    } else {
      return videoLink.aol;
    }
  };
  const handleVendorClick = (provider: EnumEmailProvider) => {
    setInformation({ ...information, provider });
  };
  const handleCloseModal = () => {
    toggleModal();
    setInformation({
      companyId: StorageAuth.CompanyId!,
    } as ISenderEmailApi);
  };
  const handleSubmit = async () => {
    if (!UtilityValidator.email(information.senderEmail)) {
      showErrorToast({ isSuccess: false, message: "Invalid email" });
      return;
    }
    setIsLoading(true);
    const data = information;
    data.appPassword = data.appPassword.replace(/\s/g, "");
    data.senderEmail = data.senderEmail.replace(/\s/g, "");
    const response = await ApiSettings.AddSenderEmail(data);
    if (response.isSuccess) {
      showSuccessToast("Saved successfully");
      handleCloseModal();
      onSave();
    }
    setIsLoading(false);
  };

  return (
    <ModalGeneric
      open={show}
      onClose={handleCloseModal}
      footer={{ visibility: false }}
      header={{ visibility: true }}
      size="lg"
    >
      <div className="container p-3">
        <div className="row g-2">
          <div className="col">
            <div className={`${!information?.provider ? "text-center" : ""}`}>
              {!information?.provider && (
                <p className="mb-2 fw-bold">Choose an email provider</p>
              )}
              <ButtonGroup className="mb-2">
                <Whisper speaker={<Tooltip>Gmail</Tooltip>} placement="bottom">
                  <IconButton
                    icon={<SvgGmail />}
                    appearance={
                      information?.provider === EnumEmailProvider.Gmail
                        ? "ghost"
                        : "default"
                    }
                    onClick={() => handleVendorClick(EnumEmailProvider.Gmail)}
                  />
                </Whisper>

                <Whisper
                  speaker={<Tooltip>Outlook</Tooltip>}
                  placement="bottom"
                >
                  <IconButton
                    icon={<SvgOutlook />}
                    appearance={
                      information?.provider === EnumEmailProvider.Outlook
                        ? "ghost"
                        : "default"
                    }
                    onClick={() => handleVendorClick(EnumEmailProvider.Outlook)}
                  />
                </Whisper>

                <Whisper speaker={<Tooltip>Yahoo</Tooltip>} placement="bottom">
                  <IconButton
                    icon={<SvgYahoo />}
                    appearance={
                      information?.provider === EnumEmailProvider.Yahoo
                        ? "ghost"
                        : "default"
                    }
                    onClick={() => handleVendorClick(EnumEmailProvider.Yahoo)}
                  />
                </Whisper>

                <Whisper speaker={<Tooltip>Aol</Tooltip>} placement="bottom">
                  <IconButton
                    icon={<SvgAol />}
                    appearance={
                      information?.provider === EnumEmailProvider.Aol
                        ? "ghost"
                        : "default"
                    }
                    onClick={() => handleVendorClick(EnumEmailProvider.Aol)}
                  />
                </Whisper>
              </ButtonGroup>
            </div>
            {information?.provider && (
              <Form ref={formRef} autoComplete="none" onSubmit={handleSubmit}>
                <TextInput
                  name="Email"
                  aria-autocomplete="none"
                  required
                  onChange={(value) =>
                    setInformation({
                      ...information,
                      senderEmail: value,
                    })
                  }
                >
                  Email
                </TextInput>
                <PasswordInput
                  name="AppPassword"
                  aria-autocomplete="none"
                  onChange={(value) =>
                    setInformation({
                      ...information,
                      appPassword: value,
                    })
                  }
                  required
                >
                  App Password
                </PasswordInput>
                <div className="text-center">
                  <Button
                    type="submit"
                    name="Save"
                    className="btn-green px-5 mt-3"
                    loading={isLoading}
                    disabled={isDisabled}
                  />
                </div>
              </Form>
            )}
          </div>
          {information?.provider && (
            <div className="col">
              <iframe
                width="560"
                height="315"
                src={getVideoLink()}
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
          )}
        </div>
      </div>
    </ModalGeneric>
  );
}
